import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase, supabaseAdmin } from '../../utils/supabaseClient';
import styled from 'styled-components';
import { FaKey, FaSignOutAlt, FaChartLine, FaPlus, FaEdit, FaTrash, FaCopy, FaRedo } from 'react-icons/fa';
import Modal from 'react-modal';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { toast } from 'react-toastify';

// Modal kök elementini ayarla
Modal.setAppElement('#root');

const DashboardContainer = styled.div`
  display: flex;
  min-height: 100vh;
  background-color: #0a0e17;
  color: #ffffff;
`;

const Sidebar = styled.div`
  width: 250px;
  background-color: #121921;
  padding: 20px 0;
  box-shadow: 2px 0 10px rgba(0, 0, 0, 0.3);
`;

const SidebarHeader = styled.div`
  padding: 0 20px 20px;
  border-bottom: 1px solid #2a3a4a;
  margin-bottom: 20px;
  
  h1 {
    color: #00ff4c;
    font-size: 1.5rem;
    margin: 0 0 10px;
  }
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  
  img {
    width: 40px;
    height: 40px;
    border-radius: 50%;
  }
  
  .user-name {
    font-weight: bold;
  }
  
  .user-role {
    font-size: 12px;
    color: #00ff4c;
  }
`;

const NavMenu = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const NavItem = styled.li`
  a {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 15px 20px;
    color: #fff;
    text-decoration: none;
    transition: background-color 0.3s;
    
    &:hover, &.active {
      background-color: #1a2a3a;
    }
    
    svg {
      color: #00ff4c;
    }
  }
`;

const Content = styled.div`
  flex: 1;
  padding: 30px;
`;

const ContentHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  
  h1 {
    font-size: 28px;
    margin: 0;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 10px;
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  border: none;
  font-weight: 500;
  transition: all 0.3s;
  
  &.primary {
    background-color: #00ff4c;
    color: #0a0e17;
    
    &:hover {
      background-color: #00cc3d;
    }
  }
  
  &.danger {
    background-color: #ff3860;
    color: white;
    
    &:hover {
      background-color: #e62b55;
    }
  }
  
  &.secondary {
    background-color: transparent;
    border: 1px solid #aaa;
    color: #aaa;
    
    &:hover {
      border-color: white;
      color: white;
    }
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 20px;
  background-color: #121921;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
  
  th, td {
    padding: 12px 15px;
    text-align: left;
  }
  
  th {
    background-color: #1a2a3a;
    color: #00ff4c;
    font-weight: 500;
  }
  
  tr:not(:last-child) {
    border-bottom: 1px solid #2a3a4a;
  }
  
  tbody tr {
    transition: background-color 0.3s;
    
    &:hover {
      background-color: #1a2a3a;
    }
  }
  
  .active {
    color: #00ff4c;
  }
  
  .inactive {
    color: #ff3860;
  }
  
  .expired {
    color: #ff9e3b;
  }
`;

const RowActions = styled.div`
  display: flex;
  gap: 8px;
  
  button {
    background: none;
    border: none;
    color: #aaa;
    cursor: pointer;
    font-size: 16px;
    padding: 4px;
    transition: color 0.3s;
    
    &:hover {
      color: white;
    }
    
    &.edit:hover {
      color: #00ff4c;
    }
    
    &.delete:hover {
      color: #ff3860;
    }
    
    &.copy:hover {
      color: #3b82f6;
    }
  }
`;

const TableFilters = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
  align-items: center;
`;

const FilterSelect = styled.select`
  background-color: #1a2a3a;
  border: 1px solid #2a3a4a;
  color: white;
  padding: 8px 12px;
  border-radius: 4px;
  min-width: 150px;
`;

const SearchInput = styled.input`
  background-color: #1a2a3a;
  border: 1px solid #2a3a4a;
  color: white;
  padding: 8px 12px;
  border-radius: 4px;
  min-width: 200px;
  
  &::placeholder {
    color: #aaa;
  }
`;

const StyledModal = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    backgroundColor: '#121921',
    border: '1px solid #2a3a4a',
    borderRadius: '8px',
    color: 'white',
    padding: '30px',
    width: '90%',
    maxWidth: '500px'
  },
  overlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.75)'
  }
};

const ModalTitle = styled.h2`
  font-size: 20px;
  color: #00ff4c;
  margin-top: 0;
  margin-bottom: 20px;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
  
  label {
    display: block;
    margin-bottom: 8px;
    font-weight: 500;
  }
  
  input, select, textarea {
    width: 100%;
    background-color: #1a2a3a;
    border: 1px solid #2a3a4a;
    color: white;
    padding: 10px;
    border-radius: 4px;
    font-size: 16px;
    
    &:focus {
      outline: none;
      border-color: #00ff4c;
    }
  }
  
  .react-datepicker-wrapper {
    width: 100%;
  }
  
  .react-datepicker__input-container input {
    width: 100%;
  }
  
  .checkbox-wrapper {
    display: flex;
    align-items: center;
    gap: 10px;
    
    input {
      width: auto;
    }
  }
`;

const ModalButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 30px;
`;

const KeyManager = () => {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filtreler
  const [productFilter, setProductFilter] = useState('Tümü');
  const [statusFilter, setStatusFilter] = useState('Tümü');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Modal durumu
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' veya 'edit'
  const [editingKey, setEditingKey] = useState(null);
  
  // Form durumu
  const [formData, setFormData] = useState({
    key: '',
    product_type: 'Spoofer',
    key_type: 'Standart',
    expiry_date: new Date(new Date().setDate(new Date().getDate() + 30)), // Varsayılan 30 gün
    description: '',
    notes: '',
    is_active: true,
    hwid: ''
  });
  
  // Anahtarları yükle
  useEffect(() => {
    fetchKeys();
  }, [productFilter, statusFilter, searchQuery]);
  
  const fetchKeys = async () => {
    try {
      setLoading(true);
      
      let query = supabase.from('license_keys').select('*');
      
      // Filtreler
      if (productFilter !== 'Tümü') {
        query = query.eq('product_type', productFilter);
      }
      
      if (statusFilter !== 'Tümü') {
        const now = new Date().toISOString();
        
        if (statusFilter === 'Aktif') {
          query = query.eq('is_active', true).gt('expiry_date', now);
        } else if (statusFilter === 'Pasif') {
          query = query.eq('is_active', false);
        } else if (statusFilter === 'Süresi Dolmuş') {
          query = query.lt('expiry_date', now);
        }
      }
      
      // Arama sorgusu
      if (searchQuery) {
        query = query.or(`key.ilike.%${searchQuery}%,description.ilike.%${searchQuery}%,hwid.ilike.%${searchQuery}%`);
      }
      
      const { data, error } = await query.order('created_date', { ascending: false });
      
      if (error) throw error;
      
      setKeys(data);
    } catch (err) {
      console.error('Anahtarlar yüklenirken hata:', err);
      setError('Anahtarlar yüklenirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };
  
  // Çıkış işlemi
  const handleLogout = async () => {
    await logout();
    navigate('/');
  };
  
  // Modal işlemleri
  const openCreateModal = () => {
    setModalMode('create');
    setFormData({
      key: '',
      product_type: 'Spoofer',
      key_type: 'Standart',
      expiry_date: new Date(new Date().setDate(new Date().getDate() + 30)),
      description: '',
      notes: '',
      is_active: true,
      hwid: ''
    });
    setIsModalOpen(true);
  };
  
  const openEditModal = (key) => {
    setModalMode('edit');
    const keyData = keys.find(k => k.key === key);
    
    if (keyData) {
      setFormData({
        ...keyData,
        expiry_date: new Date(keyData.expiry_date)
      });
      setEditingKey(keyData.key);
      setIsModalOpen(true);
    }
  };
  
  const closeModal = () => {
    setIsModalOpen(false);
    setEditingKey(null);
  };
  
  // Form input değişikliği
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      setFormData({ ...formData, [name]: checked });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };
  
  // Tarih değişikliği
  const handleDateChange = (date) => {
    setFormData({ ...formData, expiry_date: date });
  };
  
  // Benzersiz bir lisans anahtarı oluştur
  const generateKey = () => {
    // 5 bloktan oluşan XXXXX-XXXXX-XXXXX-XXXXX-XXXXX formatında bir anahtar
    const keyParts = [];
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    
    for (let i = 0; i < 5; i++) {
      let part = '';
      for (let j = 0; j < 5; j++) {
        part += chars.charAt(Math.floor(Math.random() * chars.length));
      }
      keyParts.push(part);
    }
    
    return keyParts.join('-');
  };
  
  // Form gönderme
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (modalMode === 'create') {
        // Yeni anahtar oluştur
        const newKey = {
          key: generateKey(),
          product_type: formData.product_type,
          key_type: formData.key_type,
          created_date: new Date().toISOString(),
          expiry_date: formData.expiry_date.toISOString(),
          is_active: formData.is_active,
          description: formData.description,
          notes: formData.notes,
          hwid: '',
          usage_count: 0
        };
        
        const { error } = await supabaseAdmin.from('license_keys').insert(newKey);
        
        if (error) throw error;
        
        toast.success('Lisans anahtarı başarıyla oluşturuldu.');
      } else {
        // Mevcut anahtarı güncelle
        const updatedKey = {
          product_type: formData.product_type,
          key_type: formData.key_type,
          expiry_date: formData.expiry_date.toISOString(),
          is_active: formData.is_active,
          description: formData.description,
          notes: formData.notes,
          hwid: formData.hwid
        };
        
        const { error } = await supabaseAdmin
          .from('license_keys')
          .update(updatedKey)
          .eq('key', editingKey);
        
        if (error) throw error;
        
        toast.success('Lisans anahtarı başarıyla güncellendi.');
      }
      
      closeModal();
      fetchKeys();
    } catch (err) {
      console.error('İşlem sırasında hata:', err);
      toast.error(`Hata: ${err.message}`);
    }
  };
  
  // Anahtar silme
  const deleteKey = async (key) => {
    if (!window.confirm(`"${key}" anahtarını silmek istediğinizden emin misiniz?`)) {
      return;
    }
    
    try {
      const { error } = await supabaseAdmin
        .from('license_keys')
        .delete()
        .eq('key', key);
      
      if (error) throw error;
      
      toast.success('Lisans anahtarı başarıyla silindi.');
      fetchKeys();
    } catch (err) {
      console.error('Silme işlemi sırasında hata:', err);
      toast.error(`Hata: ${err.message}`);
    }
  };
  
  // Anahtarı panoya kopyala
  const copyKeyToClipboard = (key) => {
    navigator.clipboard.writeText(key)
      .then(() => {
        toast.info('Anahtar panoya kopyalandı.');
      })
      .catch(err => {
        console.error('Kopyalama hatası:', err);
        toast.error('Anahtar kopyalanırken hata oluştu.');
      });
  };
  
  // HWID sıfırlama
  const resetHWID = async (key) => {
    if (!window.confirm(`"${key}" anahtarının HWID değerini sıfırlamak istediğinizden emin misiniz?`)) {
      return;
    }
    
    try {
      const { error } = await supabaseAdmin
        .from('license_keys')
        .update({ hwid: '' })
        .eq('key', key);
      
      if (error) throw error;
      
      toast.success('HWID başarıyla sıfırlandı.');
      fetchKeys();
    } catch (err) {
      console.error('HWID sıfırlama hatası:', err);
      toast.error(`Hata: ${err.message}`);
    }
  };
  
  return (
    <DashboardContainer>
      <Sidebar>
        <SidebarHeader>
          <h1>Kinfhertix</h1>
          <UserInfo>
            <img src={`https://cdn.discordapp.com/avatars/${currentUser?.id}/${currentUser?.avatar}.png`} alt="User Avatar" />
            <div>
              <div className="user-name">{currentUser?.username}</div>
              <div className="user-role">Admin</div>
            </div>
          </UserInfo>
        </SidebarHeader>
        
        <NavMenu>
          <NavItem>
            <Link to="/admin">
              <FaChartLine /> Dashboard
            </Link>
          </NavItem>
          <NavItem>
            <Link to="/admin/keys" className="active">
              <FaKey /> Lisans Anahtarları
            </Link>
          </NavItem>
        </NavMenu>
      </Sidebar>
      
      <Content>
        <ContentHeader>
          <h1>Lisans Anahtarları</h1>
          <ActionButtons>
            <Button className="primary" onClick={openCreateModal}>
              <FaPlus /> Yeni Anahtar Oluştur
            </Button>
            <Button className="secondary" onClick={handleLogout}>
              <FaSignOutAlt /> Çıkış Yap
            </Button>
          </ActionButtons>
        </ContentHeader>
        
        <TableFilters>
          <FilterSelect
            value={productFilter}
            onChange={(e) => setProductFilter(e.target.value)}
          >
            <option value="Tümü">Tüm Ürünler</option>
            <option value="Spoofer">Spoofer</option>
            <option value="Bypass">Bypass</option>
            <option value="Combo">Combo</option>
          </FilterSelect>
          
          <FilterSelect
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="Tümü">Tüm Durumlar</option>
            <option value="Aktif">Aktif</option>
            <option value="Pasif">Pasif</option>
            <option value="Süresi Dolmuş">Süresi Dolmuş</option>
          </FilterSelect>
          
          <SearchInput
            type="text"
            placeholder="Anahtar veya açıklama ara..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </TableFilters>
        
        {loading ? (
          <p>Anahtarlar yükleniyor...</p>
        ) : error ? (
          <p className="error">{error}</p>
        ) : (
          <Table>
            <thead>
              <tr>
                <th>Anahtar</th>
                <th>Ürün</th>
                <th>Tip</th>
                <th>Oluşturma Tarihi</th>
                <th>Geçerlilik</th>
                <th>HWID</th>
                <th>Durum</th>
                <th>Açıklama</th>
                <th>İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {keys.length === 0 ? (
                <tr>
                  <td colSpan="9" style={{ textAlign: 'center' }}>Anahtar bulunamadı.</td>
                </tr>
              ) : (
                keys.map((key) => {
                  // Durumu belirle
                  const now = new Date();
                  const expiryDate = new Date(key.expiry_date);
                  const isExpired = expiryDate < now;
                  
                  let statusClass = 'inactive';
                  let statusText = 'Pasif';
                  
                  if (key.is_active) {
                    if (isExpired) {
                      statusClass = 'expired';
                      statusText = 'Süresi Dolmuş';
                    } else {
                      statusClass = 'active';
                      statusText = 'Aktif';
                    }
                  }
                  
                  // Tarihleri formatla
                  const formatDate = (dateString) => {
                    const date = new Date(dateString);
                    return date.toLocaleDateString('tr-TR');
                  };
                  
                  return (
                    <tr key={key.key}>
                      <td>{key.key}</td>
                      <td>{key.product_type}</td>
                      <td>{key.key_type}</td>
                      <td>{formatDate(key.created_date)}</td>
                      <td>{formatDate(key.expiry_date)}</td>
                      <td>{key.hwid || 'Atanmamış'}</td>
                      <td className={statusClass}>{statusText}</td>
                      <td>{key.description}</td>
                      <td>
                        <RowActions>
                          <button className="edit" onClick={() => openEditModal(key.key)} title="Düzenle">
                            <FaEdit />
                          </button>
                          <button className="delete" onClick={() => deleteKey(key.key)} title="Sil">
                            <FaTrash />
                          </button>
                          <button className="copy" onClick={() => copyKeyToClipboard(key.key)} title="Kopyala">
                            <FaCopy />
                          </button>
                          {key.hwid && (
                            <button className="reset" onClick={() => resetHWID(key.key)} title="HWID Sıfırla">
                              <FaRedo />
                            </button>
                          )}
                        </RowActions>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </Table>
        )}
        
        {/* Anahtar Oluşturma/Düzenleme Modalı */}
        <Modal
          isOpen={isModalOpen}
          onRequestClose={closeModal}
          style={StyledModal}
          contentLabel={modalMode === 'create' ? 'Yeni Anahtar Oluştur' : 'Anahtarı Düzenle'}
        >
          <ModalTitle>
            {modalMode === 'create' ? 'Yeni Lisans Anahtarı Oluştur' : 'Lisans Anahtarını Düzenle'}
          </ModalTitle>
          
          <form onSubmit={handleSubmit}>
            {modalMode === 'edit' && (
              <FormGroup>
                <label>Anahtar</label>
                <input
                  type="text"
                  value={formData.key}
                  disabled
                />
              </FormGroup>
            )}
            
            <FormGroup>
              <label>Ürün Tipi</label>
              <select
                name="product_type"
                value={formData.product_type}
                onChange={handleInputChange}
              >
                <option value="Spoofer">Spoofer</option>
                <option value="Bypass">Bypass</option>
                <option value="Combo">Combo</option>
              </select>
            </FormGroup>
            
            <FormGroup>
              <label>Anahtar Tipi</label>
              <select
                name="key_type"
                value={formData.key_type}
                onChange={handleInputChange}
              >
                <option value="Standart">Standart</option>
                <option value="Premium">Premium</option>
                <option value="Deneme">Deneme</option>
              </select>
            </FormGroup>
            
            <FormGroup>
              <label>Son Geçerlilik Tarihi</label>
              <DatePicker
                selected={formData.expiry_date}
                onChange={handleDateChange}
                dateFormat="dd/MM/yyyy"
                minDate={new Date()}
              />
            </FormGroup>
            
            <FormGroup>
              <label>Açıklama</label>
              <input
                type="text"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
              />
            </FormGroup>
            
            <FormGroup>
              <label>Notlar</label>
              <textarea
                name="notes"
                value={formData.notes || ''}
                onChange={handleInputChange}
                rows={3}
              ></textarea>
            </FormGroup>
            
            {modalMode === 'edit' && (
              <FormGroup>
                <label>HWID</label>
                <input
                  type="text"
                  name="hwid"
                  value={formData.hwid || ''}
                  onChange={handleInputChange}
                  disabled
                />
              </FormGroup>
            )}
            
            <FormGroup>
              <div className="checkbox-wrapper">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleInputChange}
                  id="is_active"
                />
                <label htmlFor="is_active">Aktif</label>
              </div>
            </FormGroup>
            
            <ModalButtons>
              <Button type="button" className="secondary" onClick={closeModal}>
                İptal
              </Button>
              <Button type="submit" className="primary">
                {modalMode === 'create' ? 'Oluştur' : 'Güncelle'}
              </Button>
            </ModalButtons>
          </form>
        </Modal>
      </Content>
    </DashboardContainer>
  );
};

export default KeyManager; 